import { httpClientInvestment } from 'Api/microservice-http-clients';
import { isNotEmptyObject, get } from 'Helpers/utils/common';
import { K1_DOC, FARM_UPDATE_DOC, FARM_FINANCIAL_STATEMENTS_DOC } from 'Config/notification-codes';
import { INVESTMENT_STEPS_CONST as STEPS } from 'Config/constans';
import { ACCREDITATION_PROFILE_STATUS } from 'Const/investment';
import { authWSClient } from 'WSClient';
import { InvestmentClass } from 'Classes/InvestmentClass';

import {
  SET_ALL,
  UPDATE_INVESTMENT_DATA_BY_ID,
  SET_STEP_MODEL,
  SET_INVESTMENT_LIST_ITEM_STATUS,
  SET_INVESTMENT_LIST_ITEM_TAX_AS_READ,
  SET_UPDATES_DOCUMENT_AS_READ,
  SET_FINANCIAL_STATEMENTS_AS_READ,
} from './mutation-types';
import initialState from './state';

export default {
  setupNotification({ dispatch }) {
    authWSClient.subscribe(
      'investment',
      null,
      message => dispatch('handleNotification', message),
    );
  },
  handleNotification({ dispatch }, message) {
    const { message: messageBody } = message;
    if (!messageBody || !messageBody.id) return;
    const { id, ...data } = messageBody;
    dispatch('updateInvestmentById', { id, data });
  },
  updateInvestmentById({ commit }, { id, data }) {
    commit(UPDATE_INVESTMENT_DATA_BY_ID, { id, data });
  },
  setCreateModel({ commit }, createModel) {
    commit(SET_ALL, { createModel, stepModels: {} });
  },
  resetInvestment({ commit, getters }) {
    const data = {
      ...initialState(),
      // Do not reset investment list and investment profile list
      investmentProfileList: getters.investmentProfileList,
      investmentList: getters.investmentList,
      investmentListCompletedSuccessful: getters.investmentListCompletedSuccessful,
      investmentListLoaded: getters.investmentListLoaded,
      selectedProfileId: null,
    };
    commit(SET_ALL, data);
  },
  resetInvestmentList({ commit }) {
    commit(SET_ALL, {
      investmentList: [],
      investmentListLoaded: false,
      investmentListCompletedSuccessful: [],
      investmentListCompletedSuccessfulLoaded: false,
      investmentListExited: [],
      investmentListExitedLoaded: false,
    });
  },
  setStepModel({ commit, getters }, model) {
    const { investmentData } = getters;
    const stepData = investmentData[model.step];
    // For created investment when ownership is not passed profile_id is null
    // It rewrite last data or individual as default so remove it
    if (stepData && stepData.profile_id === null) delete stepData.profile_id;
    if (model.step === STEPS.AMOUNT) {
      if (investmentData.number_of_shares) {
        model._setData({ number_of_shares: investmentData.number_of_shares });
      }
    } else if (isNotEmptyObject(stepData)) {
      model._setData(stepData);
    }
    commit(SET_STEP_MODEL, model);
  },
  getInvestmentData({ commit }, id) {
    commit(SET_ALL, { investmentDataLoading: true });
    return httpClientInvestment.get(id)
      .then(({ data }) => {
        commit(SET_ALL, {
          investmentData: { ...data, id },
          investmentDataLoading: false,
          investmentDataLoaded: true,
          // Save profile id for investment for case when only profileId needed e.g. PageInvestmentComplete
          selectedProfileId: get(data, 'ownership.profile_id'),
        });
        return data;
      })
      .catch((e) => {
        commit(SET_ALL, { investmentDataLoading: false });
        throw e;
      });
  },
  setInvestmentData({ commit }, investmentData) {
    commit(SET_ALL, { investmentData });
  },
  getInvestmentList({ commit, getters }, force) {
    if (getters.investmentListLoaded && !force) return Promise.resolve();
    commit(SET_ALL, { investmentListLoading: true });
    return httpClientInvestment.get('/?state=open&order=created:desc')
      .then(({ data }) => {
        const investmentList = data.data?.map(item => new InvestmentClass(item)) || [];
        commit(SET_ALL, {
          investmentList,
          investmentListLoading: false,
          investmentListLoaded: true,
        });
        return investmentList;
      })
      .catch((e) => {
        commit(SET_ALL, { investmentListLoading: false });
        throw e;
      });
  },
  // Get Success closed investment from API
  getInvestmentCompletedSuccessfulList({ commit, state }, force) {
    if (state.investmentListCompletedSuccessfulLoaded && !force) {
      return Promise.resolve(state.investmentListCompletedSuccessful);
    }
    return httpClientInvestment.get('/?state=closed&order=closed:desc')
      .then(({ data }) => {
        const investmentListCompletedSuccessful = data.data?.map(item => new InvestmentClass(item)) || [];
        commit(SET_ALL, {
          investmentListCompletedSuccessful,
          investmentListCompletedSuccessfulLoaded: true,
        });
      });
  },
  async getInvestmentExitedList({ commit, state }, force) {
    if (state.investmentListExitedLoaded && !force) {
      return Promise.resolve(state.investmentListExited);
    }
    commit(SET_ALL, { investmentListExitedLoading: true });
    try {
      const { data } = await httpClientInvestment.get('/?state=exited');
      const investmentListExited = data.data?.map(item => new InvestmentClass({
        ...item,
        isExited: true,
      })) || [];
      commit(SET_ALL, {
        investmentListExited,
        investmentListExitedLoaded: true,
      });
      return data;
    } finally {
      commit(SET_ALL, { investmentListExitedLoading: false });
    }
  },
  setInvestmentListItemStatus({ commit }, data) {
    commit(SET_INVESTMENT_LIST_ITEM_STATUS, data);
  },
  async resetStepSignatures({ getters }) {
    // No needed to reset signatures if it not complete
    if (!getters.investmentIsSignatureResetNeeded) return;
    const model = getters.stepModels[STEPS.SIGNATURES];
    if (!model) return;
    model.$reset();
    model.$setDefaultData();
    if (model.$hasServerData) {
      await model.send(true);
    }
  },
  async resetStepAccreditation({ getters }) {
    // get accreditation step model
    const model = getters.stepModels[STEPS.ACCREDITATION];
    // if there is no model, do nothing
    if (!model) return;
    // reset model and all data
    model.$reset();
    model.$setDefaultData();
    model.$setServerData(model.$data);
    // get profile
    const profile = getters.selectedProfile;
    // if there is no profile - do nothing
    if (!profile) return;
    // if accreditation status is declined or rejected - do nothing
    if ([
      ACCREDITATION_PROFILE_STATUS.NEED_MORE_INFO,
      ACCREDITATION_PROFILE_STATUS.REJECTED,
    ].includes(profile.accreditation_status)
    && !profile.accreditation_permanent_approved) return;
    // get uploaded files from investment profile
    const profileAccreditation = get(profile, 'accreditation', {});
    // update files in model
    if (isNotEmptyObject(profileAccreditation)) {
      model.$setData(profileAccreditation);
    }
  },
  async resetStepFunding({ getters }, localOnly) {
    if (!getters.investmentIsFundingComplete) return;
    const model = getters.stepModels[STEPS.FUNDING];
    if (!model) return;
    model.$reset();
    model.$setServerData({});
    if (!localOnly) await model.resetStepSend();
  },
  // Set update docs as read but only local
  setInvestmentDocAsRead({ commit }, { id, code }) {
    let commitType = null;
    if (code === K1_DOC) commitType = SET_INVESTMENT_LIST_ITEM_TAX_AS_READ;
    else if (code === FARM_UPDATE_DOC) commitType = SET_UPDATES_DOCUMENT_AS_READ;
    else if (code === FARM_FINANCIAL_STATEMENTS_DOC) commitType = SET_FINANCIAL_STATEMENTS_AS_READ;
    else return;
    commit(commitType, id);
  },
  async setInvestmentTaxFileAsRead({ dispatch }, { id }) {
    await httpClientInvestment.get(`/document/tax/${id}`);
    dispatch('setInvestmentDocAsRead', { id, code: K1_DOC });
  },
  setInvestmentUpdatesFileAsRead({ dispatch }, id) {
    dispatch('setInvestmentDocAsRead', { id, code: FARM_UPDATE_DOC });
  },
  setInvestmentFinancialStatementsFileAsRead({ dispatch }, id) {
    dispatch('setInvestmentDocAsRead', { id, code: FARM_FINANCIAL_STATEMENTS_DOC });
  },
  reInitiateTransactionInvestmentListItem({ commit, dispatch }, id) {
    const data = { id, status: 18 };
    commit(SET_ALL, { reInitiateTransactionInvestmentListItemLoading: true });
    return httpClientInvestment.post(`/${id}/transfer`)
      .then(() => {
        commit(SET_ALL, { reInitiateTransactionInvestmentListItemLoading: false });
        dispatch('setInvestmentListItemStatus', data);
      })
      .catch((e) => {
        commit(SET_ALL, { reInitiateTransactionInvestmentListItemLoading: false });
        throw e;
      });
  },
  getInvestmentSingleData({ commit }, { id }) {
    commit(SET_ALL, { investmentSingleDataLoading: true });
    return httpClientInvestment.get(id)
      .then(({ data }) => {
        commit(SET_ALL, {
          investmentSingleData: data,
          investmentSingleDataLoading: false,
        });
      })
      .catch((e) => {
        commit(SET_ALL, {
          investmentSingleDataLoading: false,
        });
        throw e;
      });
  },
};
